import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

//PrimeNG components
import { TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { PanelModule } from 'primeng/panel';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TooltipModule } from 'primeng/tooltip';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressBarModule } from 'primeng/progressbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TreeModule } from 'primeng/tree';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FileUploadModule } from 'primeng/fileupload';
import { ScrollTopModule } from 'primeng/scrolltop';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { TabViewModule} from 'primeng/tabview';
import { CardModule } from 'primeng/card';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { ConstantsService } from './services/constants.service';
import { LoaderService } from './services/loader.service';
import { LoaderInterceptor } from './interceptors/loader-interceptor.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app/app.component';
import { ResultsViewComponent } from './components/results-view/results-view.component';
import { AdvSearchViewComponent } from './components/adv-search-view/adv-search-view.component';
import { NavTopComponent } from './components/nav-top/nav-top.component';
import { LastUpdatedComponent } from './components/last-updated/last-updated.component';
import { LoaderComponent } from './components/loader/loader.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NumberFormatDirective } from './directives/number-format.directive';
import { LocalDatePipe } from './pipes/local-date.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { AddSpaceAfterCommaPipe } from './pipes/add-space-after-comma.pipe';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ResultsEmptyViewComponent } from './components/results-empty-view/results-empty-view.component';
import { NavDateFoldersComponent } from './components/nav-date-folders/nav-date-folders.component';
import { ResultsActionsComponent } from './components/results-actions/results-actions.component';
import { SearchSimpleFormComponent } from './components/search-simple-form/search-simple-form.component';
import { ResultsTableViewComponent } from './components/results-table-view/results-table-view.component';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ResultsTodayDocsComponent } from './components/results-today-docs/results-today-docs.component';
import { ResultsFacetsComponent } from './components/results-facets/results-facets.component';
import { ResultsDetailsComponent } from './components/results-details/results-details.component';
import { ResultsSearchInfoComponent } from './components/results-search-info/results-search-info.component';
import { MoreDetailsComponent } from './components/more-details/more-details.component';
import { ResultsEmptyFacetComponent } from './components/results-empty-facet/results-empty-facet.component';
import { ResultsFacetDateComponent } from './components/results-facet-date/results-facet-date.component';
import { HomeCleanViewComponent } from './components/home-clean-view/home-clean-view.component';
import { SearchPropertyFormComponent } from './components/search-property-form/search-property-form.component';
import { ResultsSummaryViewComponent } from './components/results-summary-view/results-summary-view.component';
import { ResultsEmptyComponent } from './components/results-empty/results-empty.component';
import { ShareViewComponent } from './components/share-view/share-view.component';
import { DocPropertiesViewComponent } from './components/doc-properties-view/doc-properties-view.component';
import { SearchLoadDialogComponent } from './components/search-load-dialog/search-load-dialog.component';
import { ReportsDialogComponent } from './components/reports-dialog/reports-dialog.component';
import { DownloadDialogComponent } from './components/download-dialog/download-dialog.component';
import { DialogSaveSearchComponent } from './components/dialog-save-search/dialog-save-search.component';
import { SortSelectPanelComponent } from './components/sort-select-panel/sort-select-panel.component';
import { ColumnsSelectPanelComponent } from './components/columns-select-panel/columns-select-panel.component';
import { DialogLegacyDownloadComponent } from './components/dialog-legacy-download/dialog-legacy-download.component';
import { GlossaryViewComponent } from './components/glossary-view/glossary-view.component';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { ResultsZeroFacetComponent } from './components/results-zero-facet/results-zero-facet.component';
import { ResultsZeroViewComponent } from './components/results-zero-view/results-zero-view.component';
import { MenubarModule } from 'primeng/menubar';
import { ErrorInterceptor } from './interceptors/error-interceptor.service';
import { ErrorViewComponent } from './components/error-view/error-view.component';
import { GlobalErrorHandler } from './error-handlers/global-error-handler.service';
import { AnnouncementsViewComponent } from './components/announcements-view/announcements-view.component';
import { FileSizePipe } from './pipes/file-size.pipe';

@NgModule({
  declarations: [
    AppComponent,
    ResultsViewComponent,
    AdvSearchViewComponent,
    NavTopComponent,
    LoaderComponent,
    LastUpdatedComponent,
    NumberFormatDirective,
    LocalDatePipe,
    SafeHtmlPipe,
    AddSpaceAfterCommaPipe,
    ResultsEmptyViewComponent,
    NavDateFoldersComponent,
    ResultsActionsComponent,
    SearchSimpleFormComponent,
    ResultsTableViewComponent,
    ResultsTodayDocsComponent,
    ResultsFacetsComponent,
    ResultsDetailsComponent,
    ResultsSearchInfoComponent,
    MoreDetailsComponent,
    ResultsEmptyFacetComponent,
    ResultsFacetDateComponent,
    HomeCleanViewComponent,
    SearchPropertyFormComponent,
    ResultsSummaryViewComponent,
    ResultsEmptyComponent,
    ShareViewComponent,
    DocPropertiesViewComponent,
    SearchLoadDialogComponent,
    ReportsDialogComponent,
    DownloadDialogComponent,
    DialogSaveSearchComponent,
    SortSelectPanelComponent,
    ColumnsSelectPanelComponent,
    DialogLegacyDownloadComponent,
    GlossaryViewComponent,
    FeedbackComponent,
    ResultsZeroFacetComponent,
    ResultsZeroViewComponent,
    ErrorViewComponent,
    AnnouncementsViewComponent,
    FileSizePipe
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    MultiSelectModule,
    DropdownModule,
    PaginatorModule,
    ToolbarModule,
    ToastModule,
    AutoCompleteModule,
    PanelModule,
    ScrollPanelModule,
    TooltipModule,
    SidebarModule,
    ButtonModule,
    SplitButtonModule,
    RadioButtonModule,
    ProgressBarModule,
    DialogModule,
    ConfirmDialogModule,
    SelectButtonModule,
    TreeModule,
    ProgressSpinnerModule,
    FileUploadModule,
    ScrollTopModule,
    AccordionModule,
    CheckboxModule,
    TabViewModule,
    CardModule,
    OverlayPanelModule,
    MenubarModule
  ],
  providers: [
    ConstantsService,
    LoaderService,
    LoaderInterceptor,
    MessageService,
    DialogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    { provide: ErrorHandler, 
      useClass: GlobalErrorHandler
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
